import { Socket } from 'socket.io-client';
import {
  AgentAIConfig,
  MessagePayload,
  ConnectionState,
} from './types';
import { RootService } from './internal/rootService';
import { clientBus, internalBus } from './utils/eventBus';

/**
 * AgentAI SDK Client
 * 
 * Establishes socket connection on instantiation and provides methods
 * for sending agent/user messages.
 * 
 * @example
 * ```typescript
 * import { AgentAI } from '@koreai/agentai-node-sdk';
 * 
 * const client = new AgentAI({
 *   connection: {
 *     domainURL: 'https://your-server.com',
 *     botId: 'your-bot-id',
 *     conversationId: 'conversation-id',
 *     token: 'your-access-token',
 *     interactiveLanguage: 'en',
 *     customData: '{}',
 *     channel: 'web',
 *     sessionId: 'session-id',
 *   }
 * });
 * 
 * // Listen for connection events
 * client.on('connect', () => {
 *   console.log('Connected to AgentAI');
 * });
 * 
 * client.on('disconnect', (event) => {
 *   console.log('Disconnected:', event);
 * });
 * 
 * // Listen for message acknowledgments
 * client.on('agent_message_ack', (event) => {
 *   console.log('Agent message acknowledged:', event.payload);
 * });
 * 
 * // Send messages
 * client.sendAgentMessage({ content: 'Hello from agent' });
 * ```
 */
export class AgentAI {
  private rootService: RootService;
  constructor(config: AgentAIConfig) {
    clientBus.reset();
    internalBus.reset();
    this.rootService = new RootService(config);
  }

  on(event: string, fn: (...args: any[]) => void): this {
    clientBus.on(event, fn);
    return this;
  }

  off(event: string, fn: (...args: any[]) => void): this {
    clientBus.off(event, fn);
    return this;
  }

  once(event: string, fn: (...args: any[]) => void): this {
    clientBus.once(event, fn);
    return this;
  }

  /**
   * Disconnect from socket
   */
  disconnect(): void {
    this.rootService.disconnect();
    clientBus.reset();
    internalBus.reset();
  }

  /**
   * Get current connection state
   */
  getConnectionState(): ConnectionState {
    return this.rootService.getConnectionState();
  }

  /**
   * Check if connected
   */
  isConnected(): boolean {
    return this.rootService.isConnected();
  }

  /**
   * Check if the client was initialized with valid configuration
   */
  isValid(): boolean {
    return this.rootService.isValid();
  }

  /**
   * Get current reconnection attempt count
   */
  getReconnectAttempts(): number {
    return this.rootService.getReconnectAttempts();
  }

  /**
   * Get the raw socket instance (for advanced usage)
   */
  getSocket(): Socket | null {
    return this.rootService.getSocket();
  }

  /**
   * Send a message as an agent
   * Emits 'agent_message_ack' event as acknowledgment
   * 
   * @param payload - Message payload containing message and optional metadata
   * @returns true if message was sent, false if there was an error
   */
  sendAgentMessage(payload: MessagePayload): boolean { 
    return this.rootService.sendAgentMessage(payload);
  }

  /**
   * Send a message as a user
   * Emits 'user_message_ack' event as acknowledgment
   * 
   * @param payload - Message payload containing message and optional metadata
   * @returns true if message was sent, false if there was an error
   */
  sendUserMessage(payload: MessagePayload): boolean {
    return this.rootService.sendUserMessage(payload);
  }

  /**
   * Internal transfer
   */
  internalTransfer(payload: any, isAnonymous: boolean = false): boolean {   
    return this.rootService.internalTransfer(payload, isAnonymous);
  }

  /**
   * Generate intermittent summary
   */
  generateIntermSummary(): boolean {
    return this.rootService.generateIntermittentSummary();
  }

  /**
   * End of conversation
   */
  endOfConversation(): boolean {
    return this.rootService.endOfConversation();
  }
}
